/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';

/**
 * The useServiceBasedFeatures hook provides the Services List which are accessible to user
 * Based on the Features of authenticated user.
 *
 * ```typescript
 * import {useServiceBasedFeatures} from '@ion/packages/query/src/hooks';
 *  ...
 *  const services = useServiceBasedFeatures(serviceEnablements);
 *
 */
export const useServiceBasedFeatures = ({
  serviceEnablements,
  Features,
  serviceEnablementLabels
}): string[] => {
  const hasCARBFeatureTagged = Features.hasFeature(Features?.getCARBFeature());

  const {
    ICN,
    BLE,
    IVT,
    CARB,
    CARGO,
    DOOR,
    TPMS,
    WETS,
    BMS,
    LMS,
    WMS,
    EA,
    SENTINEL,
    MODBUS,
    TELECRYPT
  } = serviceEnablements || {};

  const {
    BLE_SERVICE,
    BMS_SERVICE,
    CARB_SERVICE,
    CARGO_SERVICE,
    DOOR_SERVICE,
    EA_SERVICE,
    ICN_SERVICE,
    IVT_SERVICE,
    LMS_SERVICE,
    MODBUS_SERVICE,
    TPMS_SERVICE,
    WETS_SERVICE,
    WMS_SERVICE,
    SENTINEL_SERVICE,
    TELECRYPT_SERVICE
  } = serviceEnablementLabels() || {};

  const allServices = {
    [BLE_SERVICE]       : BLE,
    [BMS_SERVICE]       : BMS,
    [CARGO_SERVICE]     : CARGO,
    [DOOR_SERVICE]      : DOOR,
    [EA_SERVICE]        : EA,
    [ICN_SERVICE]       : ICN,
    [IVT_SERVICE]       : IVT,
    [LMS_SERVICE]       : LMS,
    [MODBUS_SERVICE]    : MODBUS,
    [TPMS_SERVICE]      : TPMS,
    [WETS_SERVICE]      : WETS,
    [WMS_SERVICE]       : WMS,
    [SENTINEL_SERVICE]  : SENTINEL,
    [TELECRYPT_SERVICE] : TELECRYPT
  };
  
  /**
 * Check the logged in user has already tagged the CARB feature and Feature flag is ON.
 * Based on that inserting the CARB service in allServices object.
 */
  if (hasCARBFeatureTagged) {
    allServices[CARB_SERVICE]= CARB;
  }
  
  const allowedServices = useMemo(() => (
    Object.keys(allServices).filter(service => allServices[service])
  ), [allServices]);
  return allowedServices;
};

export default useServiceBasedFeatures;
